import { type PayrollLineResult, payrollDeductionItems, payrollExtraItems } from '@felix/domain';
import { ActionIcon, Button, Popover, Select, TextInput, Tooltip } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconCircleCheck,
  IconDownload,
  IconEye,
  IconFileTypePdf,
  IconLock,
  IconLockOpen,
  IconPencil,
  IconPlus,
  IconSearch,
  IconSettings,
  IconTrash,
} from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { formatDateTime, formatDay } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { sortItems } from '../purchasing/requisitions.js';
import { NoMatch, SortableTh, type SortState, TablePager } from '../purchasing/table.js';
import classes from './Payroll.module.css';
import { PaymentStatusBadge } from './PayrollForms.js';
import type { PayrollData, PayrollKind, PayrollPayment } from './types.js';
import {
  deductionLabel,
  deductionTypeLabel,
  extraLabel,
  filterPayments,
  formatMoney,
  formatPercent,
  ITEM_COLUMN,
  itemAmount,
  itemTotals,
  monthLabel,
  NO_PAYMENT_FILTER,
  type PaymentFilter,
  visibleItems,
} from './view.js';

const PAGE_SIZE = 10;

export interface SectionProps {
  data: PayrollData;
  /** Gemide hesap yazılabilir mi (yetki ve kaynak); ödeme ofiste (ADR 0066 §1). */
  can: {
    create: boolean;
    edit: boolean;
    void: boolean;
    approve: boolean;
    export: boolean;
    payments: { create: boolean; edit: boolean; void: boolean };
    /** Şirketin extra türleri (ADR 0068 §2): ofiste. */
    extraTypes?: { create: boolean; edit: boolean; void: boolean } | undefined;
    /** Şirketin kesinti türleri (ADR 0080 §2): ofiste. */
    deductionTypes?: { create: boolean; edit: boolean } | undefined;
    /** Ofisten satır talebi (ADR 0165 §2): ofiste. */
    office?: { create: boolean; edit: boolean } | undefined;
  };
  /** Seçili kayıt (pencerede açık). */
  selected: { kind: PayrollKind; id: string | null } | null;
  /** Kaydı aç; yeni extra için kişi önceden seçilebilir (ADR 0068 §5). */
  onOpen: (kind: PayrollKind, id: string | null, userId?: string) => void;
  /** Şirketin extra türleri penceresi (ofiste, yetkiliyse). */
  onExtraTypes?: (() => void) | undefined;
  /** Şirketin kesinti türleri penceresi (ofiste, yetkiliyse; ADR 0080 §2). */
  onDeductionTypes?: (() => void) | undefined;
  onRemove: (kind: PayrollKind, id: string, rowVersion: number) => void;
  onReconcile: (payment: PayrollPayment) => void;
  onPayslips: (lines: readonly PayrollLineResult[]) => void;
  onNetListPdf: () => void;
  onCsv: () => void;
  onClose: () => void;
  onReopen: () => void;
}

function usePager<T>(items: readonly T[]) {
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  return {
    page: current,
    pages,
    setPage,
    visible: items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE),
    from: (current - 1) * PAGE_SIZE + 1,
    to: Math.min(current * PAGE_SIZE, items.length),
  };
}

function useMoney() {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  return (minor: number) => formatMoney(minor, language);
}

function Pager({
  pager,
  total,
  label,
}: {
  pager: ReturnType<typeof usePager>;
  total: number;
  label: string;
}) {
  const { t } = useTranslation();
  return (
    <TablePager
      page={pager.page}
      pages={pager.pages}
      summary={t('payroll.showing', {
        from: total === 0 ? 0 : pager.from,
        to: pager.to,
        total,
      })}
      label={label}
      onPage={pager.setPage}
    />
  );
}

/* ---------- Monthly Payroll ---------- */

export function MonthlySection(props: SectionProps) {
  const { t } = useTranslation();
  const c = (key: string) => t(`payroll.columns.${key}`);
  const money = useMoney();
  const { data } = props;
  const open = data.period?.status === 'open';
  const lines = data.computation.lines;
  const pager = usePager(lines);
  const canAdd = props.can.create && open;
  const types = props.can.extraTypes;
  // Ortağın kalem sırası (M16b, ADR 0066); Extra her zaman, diğer kesinti yalnız dönemde varsa
  const items = visibleItems(lines);
  return (
    <Panel flush aria-label={t('payroll.sections.monthly')}>
      <div className={classes.toolbar}>
        <span className={classes.label}>{t('payroll.sections.monthly')}</span>
        <div className={classes.toolbarActions}>
          {props.onExtraTypes && types && (types.create || types.edit || types.void) ? (
            <Button
              variant="default"
              leftSection={<IconSettings size={16} />}
              onClick={props.onExtraTypes}
            >
              {t('payroll.actions.extraTypes')}
            </Button>
          ) : null}
          {canAdd && lines.length > 0 ? (
            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={() => props.onOpen('extras', null)}
            >
              {t('payroll.actions.addExtra')}
            </Button>
          ) : null}
          {canAdd || (props.can.office?.create && open) ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => props.onOpen('lines', null)}
            >
              {t('payroll.actions.addCrew')}
            </Button>
          ) : null}
        </div>
      </div>
      {lines.length === 0 ? (
        <EmptyState
          title={t('payroll.empty.noLines')}
          description={canAdd ? t('payroll.empty.noLinesShip') : undefined}
        />
      ) : (
        <>
          <section
            className={table.tableWrap}
            aria-label={t('payroll.sections.monthly')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${classes.autoTable}`}>
              <thead>
                <tr>
                  <th scope="col">{c('name')}</th>
                  <th scope="col" className={classes.numeric}>
                    {c('days')}
                  </th>
                  <th scope="col">{c('currency')}</th>
                  {items.map((item) => (
                    <th key={item} scope="col" className={classes.numeric}>
                      {c(ITEM_COLUMN[item])}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pager.visible.map((line) => (
                  <tr
                    key={line.id}
                    data-selected={
                      props.selected?.kind === 'lines' && props.selected.id === line.id
                        ? true
                        : undefined
                    }
                  >
                    <td>
                      <button
                        type="button"
                        className={classes.rowButton}
                        onClick={() => props.onOpen('lines', line.id)}
                      >
                        {line.name}
                      </button>
                      <span className={classes.sub}>{line.rank ?? '—'}</span>
                    </td>
                    <td className={classes.numeric}>{line.daysOnBoard}</td>
                    <td>{line.currency}</td>
                    {items.map((item) =>
                      item === 'extra' ? (
                        <td key={item} className={classes.numeric}>
                          <ExtraCell
                            line={line}
                            canAdd={canAdd}
                            canEdit={open && props.can.edit}
                            onOpen={props.onOpen}
                          />
                        </td>
                      ) : item === 'otherDeductions' ? (
                        <td key={item} className={classes.numeric}>
                          <DeductionCell
                            line={line}
                            canEdit={open && props.can.edit}
                            onOpen={props.onOpen}
                          />
                        </td>
                      ) : item === 'balance' ? (
                        <td
                          key={item}
                          className={`${classes.numeric} ${classes.net} ${line.net < 0 ? classes.negative : ''}`}
                        >
                          {money(line.net)}
                        </td>
                      ) : (
                        <td key={item} className={classes.numeric}>
                          {money(itemAmount(line, item))}
                        </td>
                      ),
                    )}
                  </tr>
                ))}
                {itemTotals(data.computation).map((total) => (
                  <tr key={total.currency} className={classes.totalRow}>
                    <td>{t('payroll.pdf.people', { count: total.people })}</td>
                    <td />
                    <td>{total.currency}</td>
                    {items.map((item) => (
                      <td key={item} className={classes.numeric}>
                        {money(total[item])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <Pager pager={pager} total={lines.length} label={t('payroll.sections.monthly')} />
        </>
      )}
    </Panel>
  );
}

/**
 * Monthly Payroll'da Extra hücresi (ADR 0068 §5): toplam; tıklayınca kalem kalem döküm, kalemi
 * açma ve kişiye extra ekleme. Extra yoksa ve eklenemiyorsa yalnız tutar.
 */
function ExtraCell({
  line,
  canAdd,
  canEdit,
  onOpen,
}: {
  line: PayrollLineResult;
  canAdd: boolean;
  canEdit: boolean;
  onOpen: SectionProps['onOpen'];
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const money = useMoney();
  const [opened, setOpened] = useState(false);
  const items = payrollExtraItems(line);
  const total = items.reduce((sum, item) => sum + item.amount, 0);
  if (items.length === 0 && !canAdd) return <>{money(total)}</>;
  const tt = (key: string) => t(key);
  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" shadow="md" withArrow>
      <Popover.Target>
        <button
          type="button"
          className={classes.extraCell}
          aria-label={`${t('payroll.columns.extra')}: ${line.name} ${money(total)}`}
          aria-expanded={opened}
          onClick={() => setOpened((o) => !o)}
        >
          {money(total)}
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <div className={classes.extraPopover}>
          <strong>
            {t('payroll.form.extras')} — {line.name}
          </strong>
          {items.length === 0 ? (
            <span className={classes.sub}>{t('payroll.form.noExtras')}</span>
          ) : (
            <ul className={classes.extraList}>
              {items.map((item) => (
                <li key={item.id}>
                  {!item.legacy && canEdit ? (
                    <button
                      type="button"
                      className={classes.rowButton}
                      onClick={() => {
                        setOpened(false);
                        onOpen('extras', item.id);
                      }}
                    >
                      {extraLabel(item, tt, language)}
                    </button>
                  ) : (
                    <span>{extraLabel(item, tt, language)}</span>
                  )}
                  <span>{money(item.amount)}</span>
                </li>
              ))}
            </ul>
          )}
          <div className={classes.extraPopoverTotal}>
            <span>{t('payroll.form.extraTotal')}</span>
            <span>
              {line.currency} {money(total)}
            </span>
          </div>
          {canAdd ? (
            <Button
              size="compact-sm"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                setOpened(false);
                onOpen('extras', null, line.userId);
              }}
            >
              {t('payroll.actions.addExtraFor', { name: line.name })}
            </Button>
          ) : null}
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

/**
 * Monthly Payroll'da Other Deductions hücresi (ADR 0080 §5): toplam; tıklayınca kalem kalem döküm
 * (satırın eski alanı ve internet, telefon, şirket türü gibi kesintiler) ve kalemi açma.
 */
function DeductionCell({
  line,
  canEdit,
  onOpen,
}: {
  line: PayrollLineResult;
  canEdit: boolean;
  onOpen: SectionProps['onOpen'];
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const money = useMoney();
  const [opened, setOpened] = useState(false);
  const items = payrollDeductionItems(line);
  const total = items.reduce((sum, item) => sum + item.amount, 0);
  if (items.length === 0) return <>{money(total)}</>;
  const tt = (key: string) => t(key);
  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" shadow="md" withArrow>
      <Popover.Target>
        <button
          type="button"
          className={classes.extraCell}
          aria-label={`${t('payroll.columns.otherDeductions')}: ${line.name} ${money(total)}`}
          aria-expanded={opened}
          onClick={() => setOpened((o) => !o)}
        >
          {money(total)}
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <div className={classes.extraPopover}>
          <strong>
            {t('payroll.columns.otherDeductions')} — {line.name}
          </strong>
          <ul className={classes.extraList}>
            {items.map((item) => (
              <li key={item.id}>
                {!item.legacy && canEdit ? (
                  <button
                    type="button"
                    className={classes.rowButton}
                    onClick={() => {
                      setOpened(false);
                      onOpen('entries', item.id);
                    }}
                  >
                    {deductionLabel(item, tt, language)}
                  </button>
                ) : (
                  <span title={item.legacy ? t('payroll.form.legacyDeduction') : undefined}>
                    {deductionLabel(item, tt, language)}
                  </span>
                )}
                <span>{money(item.amount)}</span>
              </li>
            ))}
          </ul>
          <div className={classes.extraPopoverTotal}>
            <span>{t('payroll.columns.otherDeductions')}</span>
            <span>
              {line.currency} {money(total)}
            </span>
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

/* ---------- Advances & Deductions ---------- */

export function AdvancesSection(props: SectionProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const types = props.can.deductionTypes;
  const c = (key: string) => t(`payroll.columns.${key}`);
  const money = useMoney();
  const { data } = props;
  const open = data.period?.status === 'open';
  const pager = usePager(data.entries);
  return (
    <Panel flush aria-label={t('payroll.sections.advances')}>
      <div className={classes.toolbar}>
        <span className={classes.label}>{t('payroll.sections.advances')}</span>
        <div className={classes.toolbarActions}>
          {props.onDeductionTypes && types && (types.create || types.edit) ? (
            <Button
              variant="default"
              leftSection={<IconSettings size={16} />}
              onClick={props.onDeductionTypes}
            >
              {t('payroll.actions.deductionTypes')}
            </Button>
          ) : null}
          {props.can.create && open ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => props.onOpen('entries', null)}
            >
              {t('payroll.actions.newEntry')}
            </Button>
          ) : null}
        </div>
      </div>
      {data.entries.length === 0 ? (
        <EmptyState title={t('payroll.empty.noEntries')} />
      ) : (
        <>
          <section
            className={table.tableWrap}
            aria-label={t('payroll.sections.advances')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${classes.autoTable}`}>
              <thead>
                <tr>
                  <th scope="col">{c('date')}</th>
                  <th scope="col">{c('name')}</th>
                  <th scope="col">{c('kind')}</th>
                  <th scope="col" className={classes.numeric}>
                    {c('amount')}
                  </th>
                  <th scope="col">{c('currency')}</th>
                  <th scope="col">{c('description')}</th>
                  <th scope="col">{c('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pager.visible.map((entry) => (
                  <tr
                    key={entry.id}
                    data-selected={props.selected?.id === entry.id ? true : undefined}
                  >
                    <td>{formatDay(entry.entryDay)}</td>
                    <td>{entry.name}</td>
                    <td>
                      {deductionTypeLabel(
                        {
                          type: entry.kind,
                          typeName: entry.typeName,
                          typeNameTr: entry.typeNameTr,
                        },
                        (key) => t(key),
                        language,
                      )}
                    </td>
                    <td className={classes.numeric}>{money(entry.amount)}</td>
                    <td>{entry.currency}</td>
                    <td>{entry.description ?? '—'}</td>
                    <td>
                      <span className={classes.actions}>
                        <ActionIcon
                          variant="subtle"
                          aria-label={t(
                            open && props.can.edit
                              ? 'payroll.actions.edit'
                              : 'payroll.actions.view',
                          )}
                          onClick={() => props.onOpen('entries', entry.id)}
                        >
                          {open && props.can.edit ? (
                            <IconPencil size={16} />
                          ) : (
                            <IconEye size={16} />
                          )}
                        </ActionIcon>
                        {open && props.can.void ? (
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={t('payroll.actions.remove')}
                            onClick={() => props.onRemove('entries', entry.id, entry.rowVersion)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <Pager pager={pager} total={data.entries.length} label={t('payroll.sections.advances')} />
        </>
      )}
    </Panel>
  );
}

/* ---------- Allotment ---------- */

export function AllotmentsSection(props: SectionProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';
  const c = (key: string) => t(`payroll.columns.${key}`);
  const money = useMoney();
  const { data } = props;
  const pager = usePager(data.allotments);
  return (
    <Panel flush aria-label={t('payroll.sections.allotments')}>
      <div className={classes.toolbar}>
        <span className={classes.label}>{t('payroll.sections.allotments')}</span>
        <div className={classes.toolbarActions}>
          {props.can.create ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => props.onOpen('allotments', null)}
            >
              {t('payroll.actions.newAllotment')}
            </Button>
          ) : null}
        </div>
      </div>
      {data.allotments.length === 0 ? (
        <EmptyState title={t('payroll.empty.noAllotments')} />
      ) : (
        <>
          <section
            className={table.tableWrap}
            aria-label={t('payroll.sections.allotments')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${classes.autoTable}`}>
              <thead>
                <tr>
                  <th scope="col">{c('name')}</th>
                  <th scope="col">{c('beneficiary')}</th>
                  <th scope="col">{c('relationship')}</th>
                  <th scope="col">{c('basis')}</th>
                  <th scope="col" className={classes.numeric}>
                    {c('amount')}
                  </th>
                  <th scope="col">{c('valid')}</th>
                  <th scope="col">{c('status')}</th>
                  <th scope="col">{c('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pager.visible.map((a) => (
                  <tr key={a.id} data-selected={props.selected?.id === a.id ? true : undefined}>
                    <td>{a.name}</td>
                    <td>{a.beneficiaryName}</td>
                    <td>{a.relationship ?? '—'}</td>
                    <td>{t(`payroll.bases.${a.basis}`)}</td>
                    <td className={classes.numeric}>
                      {a.basis === 'fixed'
                        ? `${a.currency} ${money(a.amount ?? 0)}`
                        : formatPercent(a.percentBp, language)}
                    </td>
                    <td>
                      {monthLabel(a.startMonth, language)} –{' '}
                      {a.endMonth ? monthLabel(a.endMonth, language) : '…'}
                    </td>
                    <td>
                      <StatusBadge tone={a.active ? 'success' : 'neutral'}>
                        {t(`payroll.active.${a.active ? 'yes' : 'no'}`)}
                      </StatusBadge>
                    </td>
                    <td>
                      <span className={classes.actions}>
                        <ActionIcon
                          variant="subtle"
                          aria-label={t(
                            props.can.edit ? 'payroll.actions.edit' : 'payroll.actions.view',
                          )}
                          onClick={() => props.onOpen('allotments', a.id)}
                        >
                          {props.can.edit ? <IconPencil size={16} /> : <IconEye size={16} />}
                        </ActionIcon>
                        {props.can.void ? (
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={t('payroll.actions.remove')}
                            onClick={() => props.onRemove('allotments', a.id, a.rowVersion)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <Pager
            pager={pager}
            total={data.allotments.length}
            label={t('payroll.sections.allotments')}
          />
        </>
      )}
    </Panel>
  );
}

/* ---------- Payslip Generator ---------- */

export function PayslipsSection(props: SectionProps) {
  const { t } = useTranslation();
  const c = (key: string) => t(`payroll.columns.${key}`);
  const money = useMoney();
  const lines = props.data.computation.lines;
  const pager = usePager(lines);
  return (
    <Panel flush aria-label={t('payroll.sections.payslips')}>
      <div className={classes.toolbar}>
        <span className={classes.label}>{t('payroll.sections.payslips')}</span>
        <div className={classes.toolbarActions}>
          {props.can.export && lines.length > 0 ? (
            <Button
              variant="default"
              leftSection={<IconFileTypePdf size={16} />}
              onClick={() => props.onPayslips(lines)}
            >
              {t('payroll.actions.allPayslips')}
            </Button>
          ) : null}
        </div>
      </div>
      {lines.length === 0 ? (
        <EmptyState title={t('payroll.empty.noLines')} />
      ) : (
        <>
          <section
            className={table.tableWrap}
            aria-label={t('payroll.sections.payslips')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${classes.autoTable}`}>
              <thead>
                <tr>
                  <th scope="col">{c('name')}</th>
                  <th scope="col">{c('rank')}</th>
                  <th scope="col">{c('currency')}</th>
                  <th scope="col" className={classes.numeric}>
                    {c('earnings')}
                  </th>
                  <th scope="col" className={classes.numeric}>
                    {c('deductions')}
                  </th>
                  <th scope="col" className={classes.numeric}>
                    {c('balance')}
                  </th>
                  <th scope="col">{c('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pager.visible.map((line) => (
                  <tr key={line.id}>
                    <td>{line.name}</td>
                    <td>{line.rank ?? '—'}</td>
                    <td>{line.currency}</td>
                    <td className={classes.numeric}>{money(line.earnings)}</td>
                    <td className={classes.numeric}>{money(line.deductions)}</td>
                    <td className={`${classes.numeric} ${classes.net}`}>{money(line.net)}</td>
                    <td>
                      {props.can.export ? (
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconDownload size={14} />}
                          aria-label={`${t('payroll.actions.payslip')} — ${line.name}`}
                          onClick={() => props.onPayslips([line])}
                        >
                          {t('payroll.actions.payslip')}
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <Pager pager={pager} total={lines.length} label={t('payroll.sections.payslips')} />
        </>
      )}
    </Panel>
  );
}

/* ---------- Payment Register & Reconciliation ---------- */

type PaymentColumn =
  | 'date'
  | 'name'
  | 'rank'
  | 'type'
  | 'amount'
  | 'method'
  | 'reference'
  | 'status';
const PAYMENT_DROP: Partial<Record<PaymentColumn, number>> = { rank: 1, method: 2, type: 3 };

const PAYMENT_SORT: Record<PaymentColumn, (p: PayrollPayment) => string | number | null> = {
  date: (p) => p.paymentDay,
  name: (p) => p.name,
  rank: (p) => p.rank,
  type: (p) => p.paymentType,
  amount: (p) => p.amount,
  method: (p) => p.method,
  reference: (p) => p.reference,
  status: (p) => p.status,
};

export function PaymentsSection(
  props: SectionProps & {
    tab: 'register' | 'reconciliation';
    onTab: (tab: 'register' | 'reconciliation') => void;
  },
) {
  const { t } = useTranslation();
  const c = (key: string) => t(`payroll.columns.${key}`);
  const money = useMoney();
  const { data } = props;
  const [filter, setFilter] = useState<PaymentFilter>(NO_PAYMENT_FILTER);
  const [sort, setSort] = useState<SortState<PaymentColumn>>({ key: 'date', dir: 'asc' });
  const effective =
    props.tab === 'reconciliation' ? { ...filter, status: 'recorded' as const } : filter;
  const items = useMemo(
    () => sortItems(filterPayments(data.payments, effective), sort.dir, PAYMENT_SORT[sort.key]),
    [data.payments, effective, sort],
  );
  const pager = usePager(items);
  // Dar tabloda önce rütbe, yöntem, tür gizlenir (`fit-columns.ts`); ortağın tarih, ad, tutar,
  // referans ve durum sütunları kalır
  const fit = useFitColumns<HTMLElement>();
  // Ortağın ekranı gibi "Amount (USD)": dönemde tek para birimi varsa başlıkta
  const currencies = new Set(data.payments.map((p) => p.currency));
  const single = currencies.size === 1 ? [...currencies][0] : null;
  const ranks = [...new Set(data.payments.map((p) => p.rank).filter((r): r is string => !!r))];
  const change = (next: Partial<PaymentFilter>) => {
    setFilter((f) => ({ ...f, ...next }));
    pager.setPage(1);
  };
  return (
    <Panel flush aria-label={t('payroll.sections.payments')}>
      <div className={classes.toolbar}>
        <fieldset className={classes.tabs} aria-label={t('payroll.sections.payments')}>
          {(['register', 'reconciliation'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              className={classes.tab}
              aria-pressed={props.tab === tab}
              onClick={() => props.onTab(tab)}
            >
              {t(`payroll.tabs.${tab}`)}
            </button>
          ))}
        </fieldset>
        <div className={classes.toolbarActions}>
          {props.can.payments.create && data.period ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => props.onOpen('payments', null)}
            >
              {t('payroll.actions.newPayment')}
            </Button>
          ) : null}
          {props.can.export ? (
            <Button
              variant="default"
              leftSection={<IconDownload size={16} />}
              onClick={props.onCsv}
            >
              {t('payroll.actions.exportCsv')}
            </Button>
          ) : null}
        </div>
      </div>
      <form
        className={classes.filters}
        aria-label={t('payroll.filters.paymentStatus')}
        onSubmit={(event) => event.preventDefault()}
      >
        <Select
          label={t('payroll.filters.paymentStatus')}
          data={[
            { value: 'all', label: t('payroll.filters.all') },
            { value: 'recorded', label: t('payroll.paymentStatus.recorded') },
            { value: 'reconciled', label: t('payroll.paymentStatus.reconciled') },
          ]}
          value={effective.status}
          readOnly={props.tab === 'reconciliation'}
          allowDeselect={false}
          onChange={(value) => change({ status: (value ?? 'all') as PaymentFilter['status'] })}
        />
        <Select
          label={t('payroll.filters.paymentType')}
          data={[
            { value: 'all', label: t('payroll.filters.all') },
            ...(['salary', 'allotment', 'final_settlement'] as const).map((v) => ({
              value: v,
              label: t(`payroll.paymentTypes.${v}`),
            })),
          ]}
          value={filter.type}
          allowDeselect={false}
          onChange={(value) => change({ type: (value ?? 'all') as PaymentFilter['type'] })}
        />
        <Select
          label={t('payroll.filters.rank')}
          data={[
            { value: 'all', label: t('payroll.filters.all') },
            ...ranks.map((r) => ({ value: r, label: r })),
          ]}
          value={filter.rank}
          allowDeselect={false}
          onChange={(value) => change({ rank: value ?? 'all' })}
        />
        <TextInput
          label={t('payroll.filters.search')}
          placeholder={t('payroll.filters.search')}
          rightSection={<IconSearch size={15} aria-hidden />}
          value={filter.query}
          onChange={(event) => change({ query: event.currentTarget.value })}
        />
        <Button
          variant="subtle"
          onClick={() => {
            setFilter(NO_PAYMENT_FILTER);
            pager.setPage(1);
          }}
        >
          {t('payroll.filters.reset')}
        </Button>
      </form>
      {data.payments.length === 0 ? (
        <EmptyState title={t('payroll.empty.noPayments')} />
      ) : items.length === 0 ? (
        <NoMatch title={t('payroll.empty.noMatch')} onClear={() => setFilter(NO_PAYMENT_FILTER)} />
      ) : (
        <>
          <section
            ref={fit}
            className={table.tableWrap}
            aria-label={t('payroll.tabs.register')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${classes.autoTable}`}>
              <thead>
                <tr>
                  {(['date', 'name', 'rank', 'type'] as const).map((key) => (
                    <SortableTh
                      key={key}
                      column={key}
                      label={c(key)}
                      sort={sort}
                      onSort={setSort}
                      drop={PAYMENT_DROP[key]}
                    />
                  ))}
                  <SortableTh
                    column="amount"
                    label={single ? `${c('amount')} (${single})` : c('amount')}
                    sort={sort}
                    onSort={setSort}
                    numeric
                  />
                  {(['method', 'reference', 'status'] as const).map((key) => (
                    <SortableTh
                      key={key}
                      column={key}
                      label={c(key)}
                      sort={sort}
                      onSort={setSort}
                      drop={PAYMENT_DROP[key]}
                    />
                  ))}
                  <th scope="col">{c('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {pager.visible.map((p) => (
                  <tr key={p.id} data-selected={props.selected?.id === p.id ? true : undefined}>
                    <td>{formatDay(p.paymentDay)}</td>
                    <td>{p.name}</td>
                    <td data-drop={PAYMENT_DROP.rank}>{p.rank ?? '—'}</td>
                    <td data-drop={PAYMENT_DROP.type}>
                      {t(`payroll.paymentTypes.${p.paymentType}`)}
                    </td>
                    <td className={classes.numeric}>
                      {single ? money(p.amount) : `${money(p.amount)} ${p.currency}`}
                    </td>
                    <td data-drop={PAYMENT_DROP.method}>{t(`payroll.methods.${p.method}`)}</td>
                    <td>{p.reference ?? '—'}</td>
                    <td>
                      <PaymentStatusBadge status={p.status} />
                    </td>
                    <td>
                      <span className={classes.actions}>
                        <Tooltip label={t('payroll.actions.view')}>
                          <ActionIcon
                            variant="subtle"
                            aria-label={`${t('payroll.actions.view')} — ${p.reference ?? p.name}`}
                            onClick={() => props.onOpen('payments', p.id)}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                        {props.can.payments.edit && p.status === 'recorded' ? (
                          <Tooltip label={t('payroll.actions.reconcile')}>
                            <ActionIcon
                              variant="subtle"
                              color="green"
                              aria-label={`${t('payroll.actions.reconcile')} — ${p.reference ?? p.name}`}
                              onClick={() => props.onReconcile(p)}
                            >
                              <IconCheck size={16} />
                            </ActionIcon>
                          </Tooltip>
                        ) : null}
                        {props.can.payments.void && p.status === 'recorded' ? (
                          <Tooltip label={t('payroll.actions.remove')}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={`${t('payroll.actions.remove')} — ${p.reference ?? p.name}`}
                              onClick={() => props.onRemove('payments', p.id, p.rowVersion)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <Pager pager={pager} total={items.length} label={t('payroll.tabs.register')} />
        </>
      )}
    </Panel>
  );
}

/* ---------- Monthly Closing ---------- */

export function ClosingSection(props: SectionProps) {
  const { t } = useTranslation();
  const money = useMoney();
  const { data } = props;
  const closed = data.period?.status === 'closed';
  const lastClose = data.events.find((e) => e.event === 'closed');
  const nameOf = (userId: string | null) =>
    data.computation.lines.find((l) => l.userId === userId)?.name ??
    data.roster.find((p) => p.userId === userId)?.name ??
    data.entries.find((e) => e.userId === userId)?.name ??
    '';
  return (
    <Panel flush aria-label={t('payroll.sections.closing')}>
      <div className={classes.toolbar}>
        <span className={classes.label}>{t('payroll.sections.closing')}</span>
        <div className={classes.toolbarActions}>
          {props.can.approve && data.period && !closed ? (
            <Button
              leftSection={<IconLock size={16} />}
              disabled={data.computation.issues.length > 0}
              onClick={props.onClose}
            >
              {t('payroll.actions.close')}
            </Button>
          ) : null}
          {props.can.approve && closed ? (
            <Button
              variant="default"
              leftSection={<IconLockOpen size={16} />}
              onClick={props.onReopen}
            >
              {t('payroll.actions.reopen')}
            </Button>
          ) : null}
        </div>
      </div>
      <div className={classes.closing}>
        <div>
          <h3 className={classes.sectionTitle}>{t('payroll.closing.checks')}</h3>
          <ul className={classes.checks}>
            {closed ? (
              <li className={classes.check} data-ok>
                <IconCircleCheck size={16} aria-hidden />
                {t('payroll.closing.closedReady', {
                  at: lastClose ? formatDateTime(lastClose.at) : '',
                  by: lastClose?.byName ?? '',
                })}
              </li>
            ) : data.computation.issues.length === 0 ? (
              <li className={classes.check} data-ok>
                <IconCircleCheck size={16} aria-hidden />
                {t('payroll.closing.ready')}
              </li>
            ) : (
              data.computation.issues.map((issue) => (
                <li
                  key={`${issue.code}-${issue.userId ?? ''}`}
                  className={classes.check}
                  data-issue
                >
                  <IconAlertTriangle size={16} aria-hidden />
                  {t(`payroll.closing.issues.${issue.code}`, { name: nameOf(issue.userId) })}
                </li>
              ))
            )}
          </ul>
          <h3 className={`${classes.sectionTitle} ${classes.spaced}`}>
            {t('payroll.closing.totals')}
          </h3>
          <section className={table.tableWrap} aria-label={t('payroll.closing.totals')}>
            <table className={`${table.table} ${classes.autoTable}`}>
              <thead>
                <tr>
                  <th scope="col">{t('payroll.columns.currency')}</th>
                  <th scope="col" className={classes.numeric}>
                    {t('payroll.closing.people')}
                  </th>
                  <th scope="col" className={classes.numeric}>
                    {t('payroll.columns.balance')}
                  </th>
                  <th scope="col" className={classes.numeric}>
                    {t('payroll.columns.allotment')}
                  </th>
                  <th scope="col" className={classes.numeric}>
                    {t('payroll.columns.payable')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.computation.totals.map((total) => (
                  <tr key={total.currency}>
                    <td>{total.currency}</td>
                    <td className={classes.numeric}>{total.people}</td>
                    <td className={classes.numeric}>{money(total.net)}</td>
                    <td className={classes.numeric}>{money(total.allotments)}</td>
                    <td className={`${classes.numeric} ${classes.net}`}>{money(total.payable)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
        <div>
          <h3 className={classes.sectionTitle}>{t('payroll.closing.history')}</h3>
          {data.events.length === 0 ? (
            <p className={classes.note}>{t('payroll.closing.noHistory')}</p>
          ) : (
            <section className={table.tableWrap} aria-label={t('payroll.closing.history')}>
              <table className={`${table.table} ${classes.autoTable}`}>
                <thead>
                  <tr>
                    <th scope="col">{t('payroll.closing.at')}</th>
                    <th scope="col">{t('payroll.columns.status')}</th>
                    <th scope="col">{t('payroll.closing.by')}</th>
                    <th scope="col">{t('payroll.closing.reason')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.events.map((e) => (
                    <tr key={e.id}>
                      <td>{formatDateTime(e.at)}</td>
                      <td>
                        {t(`payroll.closing.events.${e.event}`)}
                        {e.contentHash ? (
                          <span className={classes.sub}>
                            {t('payroll.closing.hash')} {e.contentHash.slice(0, 12)}
                          </span>
                        ) : null}
                      </td>
                      <td>{e.byName}</td>
                      <td>{e.reason ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </div>
      </div>
    </Panel>
  );
}

/* ---------- Payroll Reports ---------- */

export function ReportsSection(props: SectionProps) {
  const { t } = useTranslation();
  const money = useMoney();
  const { computation } = props.data;
  const c = (key: string) => t(`payroll.columns.${key}`);
  // Ortağın kalem sırası (M16b, ADR 0066), toplam ödenecek = kalan + Home Allotment
  const items = visibleItems(computation.lines);
  return (
    <Panel flush aria-label={t('payroll.sections.reports')}>
      <div className={classes.toolbar}>
        <span className={classes.label}>{t('payroll.pdf.netList')}</span>
        <div className={classes.toolbarActions}>
          {props.can.export && computation.lines.length > 0 ? (
            <>
              <Button leftSection={<IconFileTypePdf size={16} />} onClick={props.onNetListPdf}>
                {t('payroll.actions.exportPdf')}
              </Button>
              <Button
                variant="default"
                leftSection={<IconDownload size={16} />}
                onClick={props.onCsv}
              >
                {t('payroll.actions.exportCsv')}
              </Button>
            </>
          ) : null}
        </div>
      </div>
      {computation.lines.length === 0 ? (
        <EmptyState title={t('payroll.empty.noLines')} />
      ) : (
        <section
          className={table.tableWrap}
          aria-label={t('payroll.pdf.netList')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
          tabIndex={0}
        >
          <table className={`${table.table} ${classes.autoTable}`}>
            <thead>
              <tr>
                <th scope="col">{c('name')}</th>
                <th scope="col">{c('rank')}</th>
                <th scope="col">{c('currency')}</th>
                {items.map((item) => (
                  <th key={item} scope="col" className={classes.numeric}>
                    {c(ITEM_COLUMN[item])}
                  </th>
                ))}
                <th scope="col" className={classes.numeric}>
                  {c('payable')}
                </th>
              </tr>
            </thead>
            <tbody>
              {computation.lines.map((l) => (
                <tr key={l.id}>
                  <td>{l.name}</td>
                  <td>{l.rank ?? '—'}</td>
                  <td>{l.currency}</td>
                  {items.map((item) => (
                    <td
                      key={item}
                      className={
                        item === 'balance' ? `${classes.numeric} ${classes.net}` : classes.numeric
                      }
                    >
                      {money(itemAmount(l, item))}
                    </td>
                  ))}
                  <td className={classes.numeric}>{money(l.net + l.allotmentTotal)}</td>
                </tr>
              ))}
              {itemTotals(computation).map((total) => (
                <tr key={total.currency} className={classes.totalRow}>
                  <td>{t('payroll.pdf.total')}</td>
                  <td>{t('payroll.pdf.people', { count: total.people })}</td>
                  <td>{total.currency}</td>
                  {items.map((item) => (
                    <td key={item} className={classes.numeric}>
                      {money(total[item])}
                    </td>
                  ))}
                  <td className={classes.numeric}>{money(total.payable)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </Panel>
  );
}
